import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import {
  FaSnowflake,
  FaThermometerHalf,
  FaTools,
  FaCog,
  FaShieldAlt,
  FaClock,
  FaBuilding,
  FaHome,
  FaWrench,
  FaCheckCircle,
} from "react-icons/fa";

const Services = () => {
  const services = [
    {
      icon: <FaSnowflake className="icon-large" />,
      title: "AC Installation",
      description:
        "Professional installation of new air conditioning systems for both residential and commercial properties.",
      features: [
        "Energy-efficient systems",
        "Professional installation",
        "Warranty included",
        "Post-installation support",
      ],
    },
    {
      icon: <FaTools className="icon-large" />,
      title: "AC Repair",
      description:
        "Fast and reliable repair services for all types of air conditioning systems and components.",
      features: [
        "24/7 emergency service",
        "Diagnostic testing",
        "Parts replacement",
        "Performance optimization",
      ],
    },
    {
      icon: <FaCog className="icon-large" />,
      title: "Maintenance",
      description:
        "Regular maintenance services to keep your AC system running efficiently and extend its lifespan.",
      features: [
        "Preventive maintenance",
        "Filter replacement",
        "System cleaning",
        "Performance checks",
      ],
    },
    {
      icon: <FaThermometerHalf className="icon-large" />,
      title: "HVAC Systems",
      description:
        "Complete HVAC solutions including heating, ventilation, and air conditioning systems.",
      features: [
        "System design",
        "Energy optimization",
        "Smart controls",
        "Indoor air quality",
      ],
    },
    {
      icon: <FaShieldAlt className="icon-large" />,
      title: "Emergency Service",
      description:
        "Round-the-clock emergency AC repair services when you need immediate assistance.",
      features: [
        "24/7 availability",
        "Rapid response",
        "Emergency diagnostics",
        "Temporary solutions",
      ],
    },
    {
      icon: <FaClock className="icon-large" />,
      title: "Preventive Care",
      description:
        "Scheduled maintenance programs to prevent breakdowns and ensure optimal performance.",
      features: [
        "Regular inspections",
        "Performance monitoring",
        "Predictive maintenance",
        "Cost savings",
      ],
    },
  ];

  return (
    <section className="section-padding bg-light">
      <Container>
        <Row className="text-center mb-5">
          <Col lg={8} className="mx-auto">
            <h2 className="display-5 fw-bold mb-3 text-gradient">
              Comprehensive HVAC Services
            </h2>
            <p className="lead text-muted">
              From installation to maintenance, we provide complete air
              conditioning solutions for both commercial and residential
              properties with unmatched expertise and reliability.
            </p>
          </Col>
        </Row>

        {/* Service Categories */}
        <Row className="mb-5">
          <Col lg={6} className="mb-4">
            <Card className="h-100 border-0 shadow-sm card-hover">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <FaBuilding className="icon-medium text-primary me-3" />
                  <div>
                    <h4 className="fw-bold mb-1">Commercial Solutions</h4>
                    <p className="text-muted mb-0">
                      Large-scale HVAC systems for businesses
                    </p>
                  </div>
                </div>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    Office building cooling systems
                  </li>
                  <li className="mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    Retail space climate control
                  </li>
                  <li className="mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    Industrial facility HVAC
                  </li>
                  <li className="mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    Energy management systems
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} className="mb-4">
            <Card className="h-100 border-0 shadow-sm card-hover">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <FaHome className="icon-medium text-primary me-3" />
                  <div>
                    <h4 className="fw-bold mb-1">Residential Solutions</h4>
                    <p className="text-muted mb-0">
                      Home comfort and energy efficiency
                    </p>
                  </div>
                </div>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    Single-family home AC systems
                  </li>
                  <li className="mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    Multi-zone climate control
                  </li>
                  <li className="mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    Smart thermostat integration
                  </li>
                  <li className="mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    Energy-efficient upgrades
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Main Services Grid */}
        <Row className="g-4">
          {services.map((service, index) => (
            <Col lg={4} md={6} key={index}>
              <Card className="h-100 border-0 shadow-sm card-hover">
                <Card.Body className="p-4 text-center">
                  <div className="mb-3">{service.icon}</div>
                  <h4 className="fw-bold mb-3">{service.title}</h4>
                  <p className="text-muted mb-4">{service.description}</p>

                  <ul className="list-unstyled text-start">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="mb-2">
                        <FaCheckCircle className="text-success me-2" />
                        <small>{feature}</small>
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant="outline-primary"
                    className="mt-3 w-100"
                    style={{ borderRadius: "25px" }}
                  >
                    Learn More
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Call to Action */}
        <Row className="mt-5">
          <Col className="text-center">
            <div className="bg-primary text-white rounded-4 p-5">
              <h3 className="fw-bold mb-3">Ready to Get Started?</h3>
              <p className="lead mb-4">
                Contact us today for a free consultation and quote on your HVAC
                needs.
              </p>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Button
                  variant="warning"
                  size="lg"
                  className="fw-semibold px-4"
                  style={{ borderRadius: "30px" }}
                >
                  <FaWrench className="me-2" />
                  Get Free Quote
                </Button>
                <Button
                  variant="outline-light"
                  size="lg"
                  className="fw-semibold px-4"
                  style={{ borderRadius: "30px" }}
                >
                  <FaClock className="me-2" />
                  Schedule Service
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Services;
